// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/audit/GovernanceEventDetailDrawer.jsx

import React, { useMemo } from "react";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function titleize(value) {
  return String(value || "")
    .replace(/[_.-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderList(values) {
  if (!Array.isArray(values) || !values.length) return <em>None recorded</em>;
  return <div className="enterprise-admin-chip-list">{values.map((value) => <i key={String(value)}>{String(value)}</i>)}</div>;
}

function sourceLabel(event) {
  const table = event?.source_table || event?.source_area || "enterprise_admin";
  if (table === "enterprise_connector_authorization_events") return "Connector authorization record";
  if (table === "enterprise_governance_event_exports") return "Governance export record";
  if (table === "administrative_events") return "Administrative source record";
  return titleize(table);
}

function categoryLabel(category) {
  const labels = {
    organization: "Organization",
    users: "Users",
    roles: "Roles & Permissions",
    connectors: "Connectors",
    audit: "Audit",
  };
  return labels[String(category || "").toLowerCase()] || titleize(category);
}

function targetLabel(event) {
  return event?.target_name || event?.target_id || event?.target_type || "—";
}

function actionContext(event) {
  const eventType = String(event?.event_type || "");
  const contextMap = {
    governance_events_exported: "Audit export prepared for compliance review",
    connector_resumed: "Connector returned to active governance",
    connector_paused: "Connector paused for governance review",
    connector_reauthorization_requested: "Connector requires authorization review",
    connector_governance_updated: "Connector governance details updated",
    connector_owner_assigned: "Connector ownership assigned",
    connector_owner_removed: "Connector ownership removed",
    user_invitation_created: "User invitation created",
    user_invitation_resent: "User invitation resent",
    user_invitation_canceled: "User invitation canceled",
    user_role_assigned: "Enterprise role assigned",
    user_role_removed: "Enterprise role removed",
    user_suspended: "User access suspended",
    user_reactivated: "User access reactivated",
    organization_profile_initialized: "Organization profile created",
    organization_profile_updated: "Organization profile updated",
    organization_preferences_initialized: "Default operating preferences created",
    organization_preferences_updated: "Operating preferences updated",
  };
  return contextMap[eventType] || "Governance action recorded";
}

export default function GovernanceEventDetailDrawer({ event }) {
  const changedFields = useMemo(() => event?.changed_fields || [], [event]);
  const controls = useMemo(() => event?.control_refs || [], [event]);
  const lineage = useMemo(() => event?.links || [], [event]);

  if (!event) {
    return (
      <aside className="enterprise-admin-detail-panel enterprise-admin-governance-detail">
        <p className="enterprise-admin-eyebrow">Event Detail</p>
        <h3>Select an event</h3>
        <p className="enterprise-admin-muted">Choose a governance event to review actor, target, controls, and source traceability.</p>
      </aside>
    );
  }

  return (
    <aside className="enterprise-admin-detail-panel enterprise-admin-governance-detail">
      <div className="enterprise-admin-detail-heading">
        <p className="enterprise-admin-eyebrow">Event Detail</p>
        <h3>{event.event_title || titleize(event.event_type)}</h3>
        <p className="enterprise-admin-muted">{event.event_summary || "Compliance-ready administrative event."}</p>
      </div>

      <dl className="enterprise-admin-definition-list enterprise-admin-governance-definition-list">
        <div><dt>Area</dt><dd>{categoryLabel(event.event_category)}</dd></div>
        <div><dt>Severity</dt><dd>{titleize(event.severity || "info")}</dd></div>
        <div><dt>Actor</dt><dd>{event.actor_name || event.actor_id || "System"}</dd></div>
        <div><dt>Occurred</dt><dd>{formatDate(event.occurred_at || event.created_at)}</dd></div>
        <div><dt>Target</dt><dd>{targetLabel(event)}</dd></div>
        <div><dt>Record source</dt><dd>{sourceLabel(event)}</dd></div>
      </dl>

      <div className="enterprise-admin-governance-detail-grid">
        <section className="enterprise-admin-detail-section">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Action Context</p>
            <h4>What happened</h4>
          </div>
          <p className="enterprise-admin-muted">{actionContext(event)}</p>
        </section>

        <section className="enterprise-admin-detail-section">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Changed Fields</p>
            <h4>State delta</h4>
          </div>
          {renderList(changedFields)}
        </section>

        <section className="enterprise-admin-detail-section">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Control References</p>
            <h4>Compliance traceability</h4>
          </div>
          {renderList(controls)}
        </section>

        {event.reason ? (
          <section className="enterprise-admin-detail-section">
            <div className="enterprise-admin-card-heading compact">
              <p className="enterprise-admin-eyebrow">Reason</p>
              <h4>Operator context</h4>
            </div>
            <p className="enterprise-admin-muted">{event.reason}</p>
          </section>
        ) : null}
      </div>

      <details className="enterprise-admin-governance-technical-details">
        <summary>Technical traceability</summary>
        <div className="enterprise-admin-governance-technical-body">
          <section>
            <span>Source event</span>
            <code className="enterprise-admin-mono-wrap">{event.source_event_id || event.event_key || "Not recorded"}</code>
          </section>
          <section>
            <span>Source record</span>
            <code className="enterprise-admin-mono-wrap">{event.source_table || event.source_area || "enterprise_admin"}</code>
          </section>
          {lineage.length ? (
            <div className="enterprise-admin-lineage-list">
              {lineage.map((link) => (
                <article key={`${link.linked_source_table || link.link_type}-${link.linked_source_event_id || link.id}`}>
                  <strong>{link.link_label || titleize(link.link_type)}</strong>
                  <span>{sourceLabel({ source_table: link.linked_source_table })}</span>
                  <code className="enterprise-admin-mono-wrap">{link.linked_source_event_id || link.linked_event_id || link.id}</code>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </details>
    </aside>
  );
}
